import { z } from "zod";

import {
  freshMealPlanTypeEnum,
  pricingPlanEnum,
  type TPricingPlan,
} from "../types/enums";
import type {
  TDiscount,
  TNibblesUpsellItem,
  TSprinklesUpsellItem,
  TCapiCustomData,
} from "./primitives";
import {
  nibblesUpsellItemSchema,
  sprinklesUpsellItemSchema,
  capiCustomDataSchema,
} from "./primitives";
import {
  getDefaultDiscountForTrialPlan,
  normaliseDiscount,
  normalisePlanType,
} from "../helpers/subscription-normalisation";

/**
 * Accepts legacy persisted shapes (e.g. Kotlin MealPlans enum names, string discounts)
 * and normalises to the current store contract.
 */
const loosePlanStructureSchema = z.object({
  planType: z.union([ pricingPlanEnum, z.string() ]).optional(),
  totalOriginalPrice: z.number().optional(),
  totalDiscountedPrice: z.number().optional(),
  originalPricePerDay: z.number().optional(),
  discountedPricePerDay: z.number().optional(),
  originalPricePerPortion: z.number().optional(),
  discountedPricePerPortion: z.number().optional(),
});

type NormalisedPlanStructure = {
  planType: TPricingPlan;
  totalOriginalPrice?: number;
  totalDiscountedPrice?: number;
  originalPricePerDay?: number;
  discountedPricePerDay?: number;
  originalPricePerPortion?: number;
  discountedPricePerPortion?: number;
};

function normalisePlan(
  plan: z.infer<typeof loosePlanStructureSchema> | undefined,
  fallback: TPricingPlan,
): NormalisedPlanStructure | undefined {
  if (!plan) { return undefined; }
  return {
    planType: normalisePlanType(plan.planType, fallback),
    totalOriginalPrice: plan.totalOriginalPrice,
    totalDiscountedPrice: plan.totalDiscountedPrice,
    originalPricePerDay: plan.originalPricePerDay,
    discountedPricePerDay: plan.discountedPricePerDay,
    originalPricePerPortion: plan.originalPricePerPortion,
    discountedPricePerPortion: plan.discountedPricePerPortion,
  };
}

export const subscriptionDataSchema = z
  .object({
    discount: z.unknown().default({}),
    trial: loosePlanStructureSchema.optional(),
    ongoing: loosePlanStructureSchema.optional(),
    productType: freshMealPlanTypeEnum.optional(),
    nibblesUpsell: nibblesUpsellItemSchema.optional(),
    sprinklesUpsell: sprinklesUpsellItemSchema.optional(),
    capiCustomData: capiCustomDataSchema.optional(),
  })
  .transform((sub): {
    discount: TDiscount;
    trial?: NormalisedPlanStructure;
    ongoing?: NormalisedPlanStructure;
    productType?: z.infer<typeof freshMealPlanTypeEnum>;
    nibblesUpsell?: TNibblesUpsellItem;
    sprinklesUpsell?: TSprinklesUpsellItem;
    capiCustomData?: TCapiCustomData;
  } => {
    const trial = normalisePlan(sub.trial, "TRIAL");
    const ongoing = normalisePlan(sub.ongoing, "STANDARD");
    const trialPlan = trial?.planType ?? "TRIAL";
    const discount: TDiscount = normaliseDiscount(
      sub.discount,
      getDefaultDiscountForTrialPlan(trialPlan),
    ) ?? { discountCode: "" };
    return {
      discount,
      trial,
      ongoing,
      productType: sub.productType,
      nibblesUpsell: sub.nibblesUpsell,
      sprinklesUpsell: sub.sprinklesUpsell,
      capiCustomData: sub.capiCustomData,
    };
  });

export type TSubscriptionData = z.infer<typeof subscriptionDataSchema>;
export type TSubscriptionDataInput = z.input<typeof subscriptionDataSchema>;
